import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { exhibitionService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { getUserFacingApiError } from '../utils/apiErrors';
import {
  formatLongDate,
  getUrgencyMessage,
  getExhibitionDuration,
  getCountdownText
} from '../utils/dateUtils';
import { useSEO } from '../utils/useSEO';
import Toast from '../components/Toast';

const ExhibitionDetailPage = () => {
  const { id } = useParams();
  const [exhibition, setExhibition] = useState(null);
  const [relatedExhibitions, setRelatedExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [toast, setToast] = useState(null);

  const venueName = exhibition?.venues?.[0]?.name;
  const venueCity = exhibition?.venues?.[0]?.city;
  const seoDescription = exhibition
    ? `${exhibition.name}${venueName ? ` at ${venueName}` : ''}${venueCity ? ` in ${venueCity}` : ''}.`
    : undefined;

  useSEO({
    title: exhibition?.name,
    description: seoDescription,
  });

  useEffect(() => {
    exhibitionService.getById(id)
      .then(ex => {
        setExhibition(ex);
        // Fetch related exhibitions based on same artists or venues
        if (ex.artists && ex.artists.length > 0) {
          exhibitionService.getFiltered({ 
            artist: ex.artists[0].name 
          }).then(related => {
            // Filter out current exhibition and limit to 3
            const filtered = related.filter(r => r.id !== ex.id).slice(0, 3);
            setRelatedExhibitions(filtered);
          }).catch(err => console.error('Error fetching related:', err));
        }
      })
      .catch((err) => {
        console.error('Error fetching exhibition:', err);
        setError(
          getUserFacingApiError(err, {
            notFoundMessage: 'Event not found.',
          }),
        );
      })
      .finally(() => setLoading(false));
  }, [id]);

  const getStatusBadgeClass = (status) => {
    const classes = {
      'current': 'badge badge-current',
      'upcoming': 'badge badge-upcoming',
      'ongoing': 'badge badge-ongoing',
    };
    return classes[status] || 'badge';
  };

  const shareExhibition = (platform) => {
    if (!exhibition) return;
    
    const url = window.location.href;
    const text = `Check out "${exhibition.name}" at ${exhibition.venues?.[0]?.name || 'this venue'}`;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(exhibition.name)}&body=${encodeURIComponent(text + '\n\n' + url)}`,
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      setToast('Link copied to clipboard!');
      setShowShareMenu(false);
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
      setShowShareMenu(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !exhibition) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <ErrorMessage message={error || 'Event not found.'} />
          <div className="mt-6">
            <Link to="/search" className="text-brand hover:text-brand-dark font-semibold">
              ← Back to search
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
      {/* Back button */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link 
          to="/search" 
          className="inline-flex items-center gap-2 text-brand hover:text-brand-dark font-semibold"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to search
        </Link>
      </div>

      {/* Exhibition Details */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="card">
          <div className="p-8">
            {/* Status badge and urgency */}
            <div className="mb-4 flex flex-wrap gap-2 items-center">
              <span className={getStatusBadgeClass(exhibition.status)}>
                {exhibition.status}
              </span>
              {(() => {
                const urgencyInfo = getUrgencyMessage(exhibition);
                return urgencyInfo && (
                  <span className={`text-sm px-3 py-1 rounded-full font-medium ${
                    urgencyInfo.variant === 'warning' 
                      ? 'bg-amber-100 text-amber-800' 
                      : urgencyInfo.variant === 'success'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {urgencyInfo.text}
                  </span>
                );
              })()}
            </div>

            {/* Title and Share Button */}
            <div className="flex justify-between items-start mb-6">
              <h1 className="text-4xl font-bold text-slate-900 flex-1">
                {exhibition.name}
              </h1>
              
              {/* Share Button */}
              <div className="relative ml-4">
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="btn btn-secondary flex items-center gap-2"
                  aria-label="Share event"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share
                </button>
                
                {/* Share Menu */}
                {showShareMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-10">
                    <button
                      onClick={() => shareExhibition('twitter')}
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3"
                    >
                      <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                      </svg>
                      Twitter
                    </button>
                    <button
                      onClick={() => shareExhibition('facebook')}
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3"
                    >
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                      </svg>
                      Facebook
                    </button>
                    <button
                      onClick={() => shareExhibition('email')}
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3"
                    >
                      <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Email
                    </button>
                    <button
                      onClick={() => shareExhibition('copy')}
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3"
                    >
                      <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy Link
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Enhanced Dates */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <div className="font-semibold text-slate-900 mb-2">Dates</div>
                <div className="text-slate-700 mb-1">
                  <strong>Start:</strong> {formatLongDate(exhibition.start_date)}
                </div>
                <div className="text-slate-700 mb-2">
                  <strong>End:</strong> {formatLongDate(exhibition.end_date)}
                </div>
                {(() => {
                  const duration = getExhibitionDuration(exhibition.start_date, exhibition.end_date);
                  const countdown = exhibition.status === 'upcoming' 
                    ? getCountdownText(exhibition.start_date) 
                    : null;
                  
                  return (
                    <>
                      {duration && (
                        <div className="text-sm text-slate-600 mt-2">
                          Duration: <span className="font-medium">{duration}</span>
                        </div>
                      )}
                      {countdown && (
                        <div className="text-sm text-brand font-medium mt-2">
                          Opens in: {countdown}
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>

            {/* Artists */}
            {exhibition.artists && exhibition.artists.length > 0 ? (
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-3">Artists</h2>
                <div className="flex flex-wrap gap-2">
                  {exhibition.artists.map(artist => (
                    <div key={artist.id} className="badge bg-brand-lighter text-brand-dark">
                      {artist.name}
                    </div>
                  ))}
                </div>
              </div>
            ) : exhibition.artist_note ? (
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-3">Artists</h2>
                <p className="text-slate-700">{exhibition.artist_note}</p>
              </div>
            ) : null}

            {/* Venues */}
            {exhibition.venues && exhibition.venues.length > 0 && (
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-3">Venues</h2>
                {exhibition.venues.map(venue => (
                  <div key={venue.id} className="card bg-slate-50 p-4 mb-3">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-brand flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <h3 className="font-semibold text-slate-900">{venue.name}</h3>
                        {venue.address_line && (
                          <p className="text-sm text-slate-600">{venue.address_line}</p>
                        )}
                        <p className="text-sm text-slate-600">
                          {[venue.city, venue.state, venue.country].filter(Boolean).join(', ')}
                        </p>
                        {venue.url && (
                          <a 
                            href={venue.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-brand hover:text-brand-dark inline-flex items-center gap-1 mt-2"
                          >
                            Visit website
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Exhibition URL */}
            {exhibition.url && (
              <div className="mt-8">
                <a
                  href={exhibition.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  More Info
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            )}

            {/* Listing feedback */}
            <div className="mt-10 pt-6 border-t border-slate-200">
              <a
                href={`mailto:${process.env.REACT_APP_FEEDBACK_EMAIL || 'hello@askartie.net'}?subject=${encodeURIComponent(`Listing issue: ${exhibition.name}`)}`}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                Something wrong with this listing? Let us know.
              </a>
            </div>
          </div>
        </div>

        {/* Related Exhibitions */}
        {relatedExhibitions.length > 0 && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">You Might Also Like</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedExhibitions.map(related => (
                <Link key={related.id} to={`/events/${related.id}`} className="block h-full">
                  <div className="card hover:shadow-lg transition-shadow duration-200 h-full flex flex-col">
                    <div className="p-4 flex-1 flex flex-col">
                      <span className={getStatusBadgeClass(related.status)}>
                        {related.status}
                      </span>
                      <h3 className="text-lg font-semibold text-slate-900 mt-2 line-clamp-2">
                        {related.name}
                      </h3>
                      {related.venues && related.venues.length > 0 && (
                        <p className="text-sm text-slate-600 mt-2">
                          {related.venues[0].city || related.venues[0].name}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExhibitionDetailPage;
